import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import { Person } from "../../../models/person.model";
import { PersonService } from "../../../services/personService";

interface UseCVListActionsProps {
    refetchData: () => Promise<void>;
}

export function useCVListActions({ refetchData }: UseCVListActionsProps) {
    const navigate = useNavigate();
    const personService = new PersonService();

    const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
    const [actionLoading, setActionLoading] = useState<number | null>(null);
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
    const [personToDelete, setPersonToDelete] = useState<Person | null>(null);

    const handleSelectPerson = (person: Person) => {
        setSelectedPersonId(person.id);
    };

    const handleClosePreview = () => {
        setSelectedPersonId(null);
    };

    const handleModifyPerson = (e: React.MouseEvent, personId: number) => {
        e.stopPropagation();
        navigate(ROUTES.CV_EDITOR_WITH_ID(personId));
    };

    const handleActivityChange = async (
        e: React.MouseEvent,
        person: Person
    ) => {
        e.stopPropagation();
        try {
            setActionLoading(person.id);

            await personService.changePersonCvActivityStatus(person);

            notifications.show({
                title: "Success",
                message: `${person.firstName} ${person.lastName} is now ${!person.inactive ? "inactive" : "active"
                    }`,
                color: "green",
            });

            await refetchData();
        } catch (error) {
            console.error("Error updating person activity status:", error);
            notifications.show({
                title: "Error",
                message: "Failed to update activity status",
                color: "red",
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeletePerson = (e: React.MouseEvent, person: Person) => {
        e.stopPropagation();
        setPersonToDelete(person);
        setDeleteConfirmModalOpen(true);
    };

    const executeDeletePerson = async () => {
        if (!personToDelete) return;

        try {
            await personService.deletePerson(personToDelete);

            notifications.show({
                title: "Success",
                message: `${personToDelete.firstName} ${personToDelete.lastName}'s CV has been deleted`,
                color: "green",
            });

            setTimeout(() => {
                refetchData();
            }, 500);

            setDeleteConfirmModalOpen(false);
            setPersonToDelete(null);
        } catch (error) {
            console.error("Error deleting person:", error);
            notifications.show({
                title: "Error",
                message: "Failed to delete CV",
                color: "red",
            });
            setDeleteConfirmModalOpen(false);
            setPersonToDelete(null);
        }
    };

    const handleCopyToProposal = (id: number) => {
        // TODO: Implement copy to proposal functionality
        console.log("Copy to proposal:", id);
    };

    return {
        selectedPersonId,
        actionLoading,
        deleteConfirmModalOpen,
        personToDelete,
        setDeleteConfirmModalOpen,
        setPersonToDelete,
        handleSelectPerson,
        handleClosePreview,
        handleModifyPerson,
        handleActivityChange,
        handleDeletePerson,
        executeDeletePerson,
        handleCopyToProposal,
    };
}

